import React, { useState, useContext, useEffect } from "react";
import { Button, Typography, Tag, Badge, Image, message, Modal } from "antd";
import { EditOutlined, DeleteOutlined, LeftOutlined, RightOutlined, EyeOutlined } from "@ant-design/icons";

import "./index.css";
import { saveApi, DateFormat } from "../../../../helpers";
import Sidebar from "../../../../components/Sidebar";
import UploadImage from "../../../../components/UploadImage";
import Popup from "../../../../components/Popup";
import { StoreContext } from "../../../../context";
import { canWrite } from "../../../../helpers/permissions";

const { Title, Text } = Typography;

const formatWeightWithUnit = (weight, unit) => {
  if (!weight) return "N/A";
  
  const numericWeight = parseFloat(weight);
  
  // Remove decimal if it's a whole number
  const formattedWeight = numericWeight % 1 === 0 ? 
    Math.floor(numericWeight).toString() : 
    numericWeight.toString();
  
  // Handle different units
  switch (unit?.toLowerCase()) {
    case 'g':
    case 'gram':
    case 'grams':
      // Convert to kg if weight is greater than 1000g
      if (numericWeight >= 1000) {
        const kgWeight = numericWeight / 1000;
        const formattedKg = kgWeight % 1 === 0 ? 
          Math.floor(kgWeight).toString() : 
          kgWeight.toString();
        return `${formattedKg} kg`;
      }
      return `${formattedWeight} g`;
    
    case 'ml':
    case 'milliliter':
    case 'milliliters':
      // Convert to liters if ml is greater than 1000
      if (numericWeight >= 1000) {
        const literWeight = numericWeight / 1000;
        const formattedLiter = literWeight % 1 === 0 ? 
          Math.floor(literWeight).toString() : 
          literWeight.toString();
        return `${formattedLiter} L`;
      }
      return `${formattedWeight} ml`;
    
    case 'pcs':
    case 'piece':
    case 'pieces':
    case 'pc':
      return `${formattedWeight} pcs`;
    
    case 'kg':
    case 'kilogram':
    case 'kilograms':
      return `${formattedWeight} kg`;
    
    case 'l':
    case 'liter':
    case 'liters':
      return `${formattedWeight} L`;
    
    default:
      // If unit is not recognized, use it as is
      return unit ? `${formattedWeight} ${unit}` : `${formattedWeight} g`;
  }
};

export default function VariantSidebar({
  visible = false,
  data = {},
  editVariantHandler = () => {},
  onUploadDone = () => {},
  onClose = () => {},
}) {
  const { storeId } = useContext(StoreContext);
  const [loading, setLoading] = useState(false);
  const [display, setDisplay] = useState("");
  const [toDeleteImage, setToDeleteImage] = useState("");

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewImages, setPreviewImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Add keyboard event listener
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!previewVisible) return;
      
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handlePrevImage();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleNextImage();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        setPreviewVisible(false);
      }
    };

    // Add event listener when modal is visible
    if (previewVisible) {
      document.addEventListener('keydown', handleKeyDown);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [previewVisible, currentImageIndex, previewImages.length]);

  const deleteImageHandler = async () => {
    setLoading(true);
    const res = await saveApi(
      { product_image_id: toDeleteImage },
      "Variant Image Deleted",
      "deleteProductImage"
    );
    if (res) {
      onUploadDone();
    } else {
      setLoading(false);
    }
  };

  const handleImagePreview = (image) => {
    const images = data.images || [];
    const currentIndex = images.findIndex((img) => img.url === image.url);

    setPreviewImages(images);
    setPreviewImage(image.url);
    setCurrentImageIndex(currentIndex);
    setPreviewVisible(true);
  };

  const handlePrevImage = () => {
    if (previewImages.length <= 1) return;
    const newIndex =
      currentImageIndex > 0 ? currentImageIndex - 1 : previewImages.length - 1;
    setCurrentImageIndex(newIndex);
    setPreviewImage(previewImages[newIndex].url);
  };

  const handleNextImage = () => {
    if (previewImages.length <= 1) return;
    const newIndex =
      currentImageIndex < previewImages.length - 1 ? currentImageIndex + 1 : 0;
    setCurrentImageIndex(newIndex);
    setPreviewImage(previewImages[newIndex].url);
  };

  return (
    <>
      <Modal
        visible={previewVisible}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width="90vw"
        style={{ maxWidth: "800px" }}
        centered
        bodyStyle={{ padding: 0 }}
      >
        <div style={{ position: "relative", textAlign: "center" }}>
          <Image
            src={previewImage}
            style={{
              width: "100%",
              maxHeight: "70vh",
              objectFit: "contain",
            }}
            preview={false}
          />

          {previewImages.length > 1 && (
            <>
              <Button
                type="text"
                icon={<LeftOutlined />}
                onClick={handlePrevImage}
                style={{
                  position: "absolute",
                  left: "16px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(0,0,0,0.5)",
                  color: "white",
                  border: "none",
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />

              <Button
                type="text"
                icon={<RightOutlined />}
                onClick={handleNextImage}
                style={{
                  position: "absolute",
                  right: "16px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(0,0,0,0.5)",
                  color: "white",
                  border: "none",
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
            </>
          )}

          {previewImages.length > 1 && (
            <div
              style={{
                position: "absolute",
                bottom: "16px",
                left: "50%",
                transform: "translateX(-50%)",
                backgroundColor: "rgba(0,0,0,0.7)",
                color: "white",
                padding: "4px 12px",
                borderRadius: "16px",
                fontSize: "14px",
              }}
            >
              {currentImageIndex + 1} / {previewImages.length}
            </div>
          )}

        </div>
      </Modal>

      {toDeleteImage && !loading && (
        <Popup
          saveText="Yes, Delete"
          title="Delete Variant Image"
          saveHandler={deleteImageHandler}
          visible={toDeleteImage && !loading ? true : false}
          cancelHandler={() => setToDeleteImage("")}
        >
          <Title style={{ fontSize: 18 }}>
            Are you sure to delete the Image ?
          </Title>
          <Text type="danger">This action cannot be reverted.</Text>
        </Popup>
      )}
      <Sidebar
        title="Variant Details"
        visible={visible}
        onClose={onClose}
        loading={loading}
        footer={
          <div
            style={{
              display: "flex",
              gap: "12px",
              padding: "16px 0",
            }}
          >
            {canWrite() && (
              <Button
                icon={<EditOutlined />}
                onClick={editVariantHandler}
                type="primary"
                size="large"
                style={{
                  width: "100%",
                  height: "48px",
                  borderRadius: "8px",
                }}
              >
                Edit {storeId ? "Stock" : "Variant"} Details
              </Button>
            )}
          </div>
        }
      >
        <div style={{ padding: "0 4px" }}>
          <div
            style={{
              marginBottom: "16px",
              backgroundColor: "#F7F7F7",
              borderRadius: "12px",
              padding: "12px",
            }}
          >
            <div
              style={{
                marginBottom: "12px",
                display: "flex",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: 500,
                  padding: "4px 12px",
                  textTransform: "uppercase",
                  borderRadius: "6px",
                  backgroundColor:
                    (data.store_is_active === 0 && storeId) ||
                    data.variant_is_active === 0
                      ? "#fff2f0"
                      : "#f6ffed",
                  color:
                    (data.store_is_active === 0 && storeId) ||
                    data.variant_is_active === 0
                      ? "#ff4d4f"
                      : "#52c41a",
                  border: `1px solid ${
                    (data.store_is_active === 0 && storeId) ||
                    data.variant_is_active === 0
                      ? "#ffccc7"
                      : "#b7eb8f"
                  }`,
                }}
              >
                {(data.store_is_active === 0 && storeId) ||
                data.variant_is_active === 0
                  ? "Inactive"
                  : "Active"}
              </span>
              {storeId && data.variant_status && (
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                    padding: "4px 12px",
                    borderRadius: "6px",
                    textTransform: "capitalize",
                    backgroundColor:
                      data.variant_status === "finishing soon"
                        ? "#fff7e6"
                        : data.variant_status === "out of stock"
                        ? "#fff2f0"
                        : "#f6ffed",
                    color:
                      data.variant_status === "finishing soon"
                        ? "#fa8c16"
                        : data.variant_status === "out of stock"
                        ? "#ff4d4f"
                        : "#52c41a",
                    border: `1px solid ${
                      data.variant_status === "finishing soon"
                        ? "#ffd591"
                        : data.variant_status === "out of stock"
                        ? "#ffccc7"
                        : "#b7eb8f"
                    }`,
                  }}
                >
                  {data.variant_status}
                </span>
              )}
            </div>

            {display ? (
              <div
                style={{ position: "relative", cursor: "pointer" }}
                onClick={() => {
                  const imageObj = data.images?.find(
                    (img) => img.url === display
                  );
                  if (imageObj) handleImagePreview(imageObj);
                }}
              >
                <Image
                  width="100%"
                  height={280}
                  style={{
                    borderRadius: "12px",
                    objectFit: "cover",
                  }}
                  src={display}
                  preview={false}
                />
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0,0,0,0)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: 0,
                    transition: "all 0.3s ease",
                    borderRadius: "12px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = 1;
                    e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = 0;
                    e.currentTarget.style.backgroundColor = "rgba(0,0,0,0)";
                  }}
                >
                  <EyeOutlined style={{ color: "white", fontSize: "24px" }} />
                </div>
              </div>
            ) : (
              data.images &&
              data.images.length > 0 && (
                <div
                  style={{ position: "relative", cursor: "pointer" }}
                  onClick={() => handleImagePreview(data.images[0])}
                >
                  {/* <Image
                    width="100%"
                    height={280}
                    style={{
                      borderRadius: "12px",
                      objectFit: "cover",
                    }}
                    src={data.images[0].url}
                    preview={false}
                  /> */}
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(0,0,0,0)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: 0,
                      transition: "all 0.3s ease",
                      borderRadius: "12px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = 1;
                      e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.3)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = 0;
                      e.currentTarget.style.backgroundColor = "rgba(0,0,0,0)";
                    }}
                  >
                    <EyeOutlined style={{ color: "white", fontSize: "24px" }} />
                  </div>
                </div>
              )
            )}

            {!storeId && (
              <div style={{ marginTop: "16px" }}>
                <UploadImage
                  title="Add Variant Image"
                  type="product"
                  apiName="uploadProductImage"
                  onUploadDone={onUploadDone}
                  type_id={data.product_id}
                  variant_id={data.variant_id}
                  setLoading={setLoading}
                  fileList={
                    data.images && data.images.length > 0 ? data.images : []
                  }
                  done={
                    (data.images && data.images.length > 2) || storeId
                      ? true
                      : false
                  }
                  onRemove={(image) =>
                    storeId
                      ? message.warn(
                          "You do not have access to delete this image."
                        )
                      : setToDeleteImage(image.image_id)
                  }
                  onPreview={handleImagePreview}
                  style={{
                    borderRadius: "12px",
                  }}
                />
              </div>
            )}

            {storeId && data.images && data.images.length > 0 && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                  gap: "12px",
                  marginTop: "16px",
                }}
              >
                {data.images.map((image, index) => (
                  <div
                    key={index}
                    style={{
                      position: "relative",
                      borderRadius: "8px",
                      overflow: "hidden",
                      cursor: "pointer",
                      border: "1px solid #f0f0f0",
                    }}
                    onClick={() => handleImagePreview(image)}
                  >
                    <Image
                      width="100%"
                      height={120}
                      src={image.url}
                      style={{ objectFit: "cover" }}
                      preview={false}
                    />
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0,0,0,0)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: 0,
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = 1;
                        e.currentTarget.style.backgroundColor =
                          "rgba(0,0,0,0.3)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = 0;
                        e.currentTarget.style.backgroundColor =
                          "rgba(0,0,0,0)";
                      }}
                    >
                      <EyeOutlined
                        style={{ color: "white", fontSize: "24px" }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ marginBottom: "12px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "8px",
              }}
            >
              <Title
                style={{
                  margin: 0,
                  fontSize: "24px",
                  fontWeight: 600,
                  lineHeight: 1.2,
                  flex: 1,
                }}
                level={3}
              >
                {data.product_name}
              </Title>
            </div>

            <Text
              type="secondary"
              style={{
                fontSize: "14px",
                display: "block",
                marginBottom: "8px",
              }}
            >
              {data.category_name && <>Category: {data.category_name}</>}
              {data.sub_category_name && (
                <> • Sub Category: {data.sub_category_name}</>
              )}
              {data.sub_category_group_name && (
                <> • Group: {data.sub_category_group_name}</>
              )}
            </Text>

            <Text type="secondary" style={{ fontSize: "14px" }}>
              SKU: {data.variant_sku} • Created: {DateFormat(data.created_at)}
            </Text>
          </div>

          <div style={{ marginBottom: "16px" }}>
            <Text
              style={{
                fontSize: "16px",
                fontWeight: 600,
                display: "block",
                marginBottom: "8px",
              }}
            >
              Pricing
            </Text>
            {data.price === "0.00" ? (
              <Text strong style={{ fontSize: "28px", color: "#000" }}>
                ₹ {data.mrp}
              </Text>
            ) : (
              <div>
                <Text strong style={{ fontSize: "28px", color: "#000" }}>
                  ₹ {data.price}
                </Text>
                {data.mrp && data.mrp !== data.price && (
                  <>
                    <Text
                      style={{
                        textDecorationLine: "line-through",
                        fontSize: "16px",
                        marginLeft: "12px",
                        color: "#999",
                      }}
                    >
                      ₹ {data.mrp}
                    </Text>
                    <Badge
                      count={`${(
                        (100 * (data.mrp - data.price)) /
                        data.mrp
                      ).toFixed(0)}% Off`}
                      style={{
                        backgroundColor: "#52c41a",
                        marginLeft: 12,
                        borderRadius: "4px",
                      }}
                    />
                  </>
                )}
              </div>
            )}

            <div
              style={{
                marginTop: "12px",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
              }}
            >
              {data.cost_price && data.cost_price !== "0.00" && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Text style={{ fontSize: "14px", color: "#666" }}>
                    Cost Price:
                  </Text>
                  <Text style={{ fontSize: "14px", fontWeight: 500 }}>
                    ₹ {data.cost_price}
                  </Text>
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Text style={{ fontSize: "14px", color: "#666" }}>MRP:</Text>
                <Text style={{ fontSize: "14px", fontWeight: 500 }}>
                  ₹ {data.mrp === "0.00" ? "0" : data.mrp}
                </Text>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Text style={{ fontSize: "14px", color: "#666" }}>
                  Sell Price:
                </Text>
                <Text
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#52c41a",
                  }}
                >
                  ₹ {data.price === "0.00" ? "0" : data.price}
                </Text>
              </div>
              {data.price !== "0.00" &&
                data.mrp !== "0.00" &&
                parseFloat(data.mrp) > parseFloat(data.price) && (
                  <div
                    style={{
                      marginTop: "8px",
                      padding: "4px 8px",
                      backgroundColor: "#f6ffed",
                      border: "1px solid #b7eb8f",
                      borderRadius: "4px",
                      textAlign: "center",
                    }}
                  >
                    <Text
                      style={{
                        fontSize: "12px",
                        color: "#52c41a",
                        fontWeight: 500,
                      }}
                    >
                      {(
                        (100 * (data.mrp - data.price)) /
                        data.mrp
                      ).toFixed(0)}
                      % Discount
                    </Text>
                  </div>
                )}
            </div>
          </div>

          {storeId && (
            <div style={{ marginBottom: "16px" }}>
              <Text
                style={{
                  fontSize: "16px",
                  fontWeight: 600,
                  display: "block",
                  marginBottom: "4px",
                }}
              >
                Stock Information
              </Text>
              <Text style={{ color: "#666", fontSize: "14px" }}>
                {data.variant_stock} packets available
              </Text>
              {data.variant_status && (
                <div style={{ marginTop: "8px" }}>
                  <Text style={{ fontSize: "14px", color: "#666" }}>
                    Status:
                  </Text>
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      padding: "2px 8px",
                      borderRadius: "4px",
                      textTransform: "capitalize",
                      marginLeft: "8px",
                      backgroundColor:
                        data.variant_status === "finishing soon"
                          ? "#fff7e6"
                          : data.variant_status === "out of stock"
                          ? "#fff2f0"
                          : "#f6ffed",
                      color:
                        data.variant_status === "finishing soon"
                          ? "#fa8c16"
                          : data.variant_status === "out of stock"
                          ? "#ff4d4f"
                          : "#52c41a",
                    }}
                  >
                    {data.variant_status}
                  </span>
                </div>
              )}
            </div>
          )}

          <div style={{ marginBottom: "16px" }}>
            <Text
              style={{
                fontSize: "16px",
                fontWeight: 600,
                display: "block",
                marginBottom: "12px",
              }}
            >
              Variant Information
            </Text>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                marginBottom: "12px",
              }}
            >
              <span
                style={{
                  fontSize: "14px",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  backgroundColor: "#e6f7ff",
                  color: "#1890ff",
                  border: "2px solid #1890ff",
                  display: "inline-block",
                }}
              >
                {data.variant_name}
              </span>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              {data.description && (
                <div>
                  <Text
                    style={{
                      fontSize: "14px",
                      color: "#666",
                      fontWeight: 500,
                    }}
                  >
                    Description:
                  </Text>
                  <Text style={{ fontSize: "14px", marginLeft: "8px" }}>
                    {data.description}
                  </Text>
                </div>
              )}

              <div>
                <Text
                  style={{
                    fontSize: "14px",
                    color: "#666",
                    fontWeight: 500,
                  }}
                >
                  Product ID:
                </Text>
                <Text style={{ fontSize: "14px", marginLeft: "8px" }}>
                  {data.product_id}
                </Text>
              </div>

              <div>
                <Text
                  style={{
                    fontSize: "14px",
                    color: "#666",
                    fontWeight: 500,
                  }}
                >
                  Variant ID:
                </Text>
                <Text style={{ fontSize: "14px", marginLeft: "8px" }}>
                  {data.variant_id}
                </Text>
              </div>

              {data.store_name && (
                <div>
                  <Text
                    style={{
                      fontSize: "14px",
                      color: "#666",
                      fontWeight: 500,
                    }}
                  >
                    Store:
                  </Text>
                  <Text style={{ fontSize: "14px", marginLeft: "8px" }}>
                    {data.store_name}
                  </Text>
                </div>
              )}
            </div>
          </div>

          <div style={{ marginBottom: "16px" }}>
            <Text
              style={{
                fontSize: "16px",
                fontWeight: 600,
                display: "block",
                marginBottom: "8px",
              }}
            >
              Weight
            </Text>
            <span
              style={{
                fontSize: "14px",
                padding: "6px 12px",
                borderRadius: "6px",
                backgroundColor: "var(--component-background, #fafafa)",
                border: "1px solid var(--border-color, #d9d9d9)",
                color: "var(--text-color-secondary, #666)",
                display: "inline-block",
              }}
            >
              {formatWeightWithUnit(data.weight, data.unit)}
            </span>
          </div>
        </div>
      </Sidebar>
    </>
  );
}
